import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageAdmin = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPrivilegesModal, setShowPrivilegesModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminPrivileges, setAdminPrivileges] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    password: '',
    hostel_id: '',
    hostel_name: '',
    designation: 'Hostel Administrator',
    joining_date: new Date().toISOString().split('T')[0],
    responsibilities: '',
    contact_hours: '9:00 AM - 6:00 PM',
    office_location: ''
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchAdmins();
      fetchHostels();
      fetchAvailablePages();
    }
  }, [userInfo]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/admins/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setAdmins(data.admins || []);
      } else {
        setError(data.message || 'Failed to load admins');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const fetchAvailablePages = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/available-pages/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setAvailablePages(data.pages || []);
      }
    } catch (err) {
      console.error('Error fetching available pages:', err);
    }
  };

  const fetchAdminPrivileges = async (adminUserId) => {
    try {
      const response = await fetch(`${API_URL}/superadmin/admin-privileges/${userInfo.userId}/${adminUserId}`);
      const data = await response.json();

      if (data.success) {
        setAdminPrivileges(data.privileges || []);
      } else {
        alert(data.message || 'Failed to load privileges');
      }
    } catch (err) {
      console.error('Error fetching admin privileges:', err);
      alert('Failed to load admin privileges');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'hostel_id') {
      const selectedHostel = hostels.find(h => h.hostel_id === parseInt(value));
      if (selectedHostel) {
        setFormData(prev => ({
          ...prev,
          hostel_name: selectedHostel.hostel_name
        }));
      }
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();

    if (formData.phone_number.length !== 10) {
      alert('Phone number must be 10 digits');
      return;
    }

    if (formData.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/superadmin/admin/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        alert('Admin added successfully!');
        setShowAddModal(false);
        resetForm();
        fetchAdmins();
      } else {
        alert(data.message || 'Failed to add admin');
      }
    } catch (err) {
      console.error('Error adding admin:', err);
      alert('Failed to add admin');
    }
  };

  const handleEditAdmin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${API_URL}/superadmin/admin/${userInfo.userId}/${selectedAdmin.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Admin updated successfully!');
        setShowEditModal(false);
        setSelectedAdmin(null);
        resetForm();
        fetchAdmins();
      } else {
        alert(data.message || 'Failed to update admin');
      }
    } catch (err) {
      console.error('Error updating admin:', err);
      alert('Failed to update admin');
    }
  };

  const handleDeactivateAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to deactivate this admin?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/superadmin/admin/${userInfo.userId}/${adminId}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Admin deactivated successfully!');
        fetchAdmins();
      } else {
        alert(data.message || 'Failed to deactivate admin');
      }
    } catch (err) {
      console.error('Error deactivating admin:', err);
      alert('Failed to deactivate admin');
    }
  };

  const handlePrivilegeChange = (pageId, field, value) => {
    setAdminPrivileges(prev => {
      const existing = prev.find(p => p.page_id === pageId);
      if (existing) {
        return prev.map(p => p.page_id === pageId ? { ...p, [field]: value } : p);
      } else {
        const page = availablePages.find(p => p.page_id === pageId);
        return [...prev, {
          page_id: pageId,
          page_name: page?.page_name,
          page_route: page?.page_route,
          page_icon: page?.page_icon,
          can_view: field === 'can_view' ? value : 0,
          can_edit: field === 'can_edit' ? value : 0,
          can_delete: field === 'can_delete' ? value : 0,
          can_approve: field === 'can_approve' ? value : 0
        }];
      }
    });
  };

  const saveAdminPrivileges = async () => {
    try {
      const response = await fetch(
        `${API_URL}/superadmin/admin-privileges/${userInfo.userId}/${selectedAdmin.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ privileges: adminPrivileges })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Privileges updated successfully!');
        setShowPrivilegesModal(false);
        setSelectedAdmin(null);
      } else {
        alert(data.message || 'Failed to update privileges');
      }
    } catch (err) {
      console.error('Error updating privileges:', err);
      alert('Failed to update privileges');
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      full_name: admin.full_name,
      phone_number: admin.phone_number,
      email: admin.email,
      password: '',
      hostel_id: admin.hostel_id,
      hostel_name: admin.hostel_name,
      designation: admin.designation || 'Hostel Administrator',
      joining_date: admin.joining_date || new Date().toISOString().split('T')[0],
      responsibilities: admin.responsibilities || '',
      contact_hours: admin.contact_hours || '9:00 AM - 6:00 PM',
      office_location: admin.office_location || ''
    });
    setShowEditModal(true);
  };

  const openPrivilegesModal = async (admin) => {
    setSelectedAdmin(admin);
    await fetchAdminPrivileges(admin.user_id);
    setShowPrivilegesModal(true);
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone_number: '',
      email: '',
      password: '',
      hostel_id: '',
      hostel_name: '',
      designation: 'Hostel Administrator',
      joining_date: new Date().toISOString().split('T')[0],
      responsibilities: '',
      contact_hours: '9:00 AM - 6:00 PM',
      office_location: ''
    });
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = admin.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.phone_number.includes(searchQuery) ||
      admin.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.hostel_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesHostel = filterHostel === 'all' || admin.hostel_name === filterHostel;
    return matchesSearch && matchesHostel;
  });

  const getPrivilegeStatus = (privilege) => {
    if (!privilege) return { view: false, edit: false, delete: false, approve: false };
    return {
      view: privilege.can_view === 1,
      edit: privilege.can_edit === 1,
      delete: privilege.can_delete === 1,
      approve: privilege.can_approve === 1
    };
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-admin"
      pageTitle="Manage Admins 👨‍💼"
    >
      <style>{`
        .controls-section {
          display: flex;
          gap: 16px;
          margin-bottom: 32px;
          align-items: center;
          flex-wrap: wrap;
        }

        .search-box {
          flex: 1;
          position: relative;
          min-width: 300px;
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: rgba(43,103,119,0.5);
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          padding: 16px 18px 16px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 500;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .filter-select {
          padding: 16px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
          min-width: 180px;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .add-btn {
          padding: 16px 28px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border: none;
          border-radius: 14px;
          color: white;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          white-space: nowrap;
        }

        .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        .admins-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 24px;
        }

        .admin-card {
          background: white;
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 6px 24px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.1);
          transition: all 0.3s;
        }

        .admin-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .admin-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 2px solid rgba(82,171,152,0.1);
        }

        .admin-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          font-weight: 900;
          color: white;
          box-shadow: 0 6px 20px rgba(43,103,119,0.3);
        }

        .admin-info {
          flex: 1;
        }

        .admin-name {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .admin-designation {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .admin-details {
          display: grid;
          gap: 14px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .detail-label {
          font-size: 13px;
          font-weight: 600;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          word-break: break-word;
        }

        .admin-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn-privileges,
        .action-btn-edit,
        .action-btn-delete {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn-privileges {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .action-btn-privileges:hover {
          background: rgba(34,197,94,0.25);
          transform: translateY(-2px);
        }

        .action-btn-edit {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-edit:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-delete {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: rgba(239,68,68,0.25);
          transform: translateY(-2px);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .status-inactive {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-large {
          max-width: 900px;
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }

        .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel,
        .btn-submit {
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }

        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .privileges-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          margin-top: 20px;
        }

        .privileges-table thead th {
          background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
          padding: 16px;
          text-align: left;
          font-size: 14px;
          font-weight: 800;
          color: #2b6777;
          border-bottom: 2px solid rgba(82,171,152,0.2);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .privileges-table thead th:first-child {
          border-top-left-radius: 12px;
        }

        .privileges-table thead th:last-child {
          border-top-right-radius: 12px;
        }

        .privileges-table tbody td {
          padding: 14px 16px;
          border-bottom: 1px solid rgba(82,171,152,0.1);
          font-size: 14px;
        }

        .privileges-table tbody tr:hover {
          background: rgba(82,171,152,0.03);
        }

        .privilege-category-header {
          background: linear-gradient(135deg, rgba(82,171,152,0.1) 0%, rgba(43,103,119,0.08) 100%);
          font-weight: 800;
          color: #2b6777;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 1px;
          padding: 12px 16px !important;
        }

        .page-name {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          color: #2b6777;
        }

        .page-icon {
          font-size: 20px;
        }

        .checkbox-cell {
          text-align: center;
        }

        .privilege-checkbox {
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #52ab98;
        }

        .modal-subtitle {
          color: rgba(43,103,119,0.6);
          font-size: 14px;
          margin-bottom: 20px;
          font-weight: 500;
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .admins-grid {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .controls-section {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .search-box {
            width: 100%;
          }

          .admin-actions {
            flex-direction: column;
          }

          .privileges-table {
            font-size: 12px;
          }

          .privileges-table thead th,
          .privileges-table tbody td {
            padding: 10px 8px;
          }
        }
      `}</style>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search admins by name, phone, email, or hostel..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_name}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <button className="add-btn" onClick={() => setShowAddModal(true)}>
          <span>➕</span>
          Add New Admin
        </button>
      </div>

      {/* Admins Grid */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading admins...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredAdmins.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👨‍💼</div>
          <div className="empty-state-text">No admins found</div>
        </div>
      ) : (
        <div className="admins-grid">
          {filteredAdmins.map((admin) => (
            <div key={admin.user_id} className="admin-card">
              <div className="admin-header">
                <div className="admin-icon">
                  {admin.full_name.charAt(0)}
                </div>
                <div className="admin-info">
                  <div className="admin-name">{admin.full_name}</div>
                  <span className="admin-designation">{admin.designation || 'Admin'}</span>
                </div>
              </div>

              <div className="admin-details">
                <div className="detail-item">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{admin.phone_number}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{admin.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Hostel</span>
                  <span className="detail-value">{admin.hostel_name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Joined</span>
                  <span className="detail-value">
                    {admin.joining_date ? new Date(admin.joining_date).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${admin.is_active ? 'status-active' : 'status-inactive'}`}>
                    {admin.is_active ? '✓ Active' : '✗ Inactive'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Contact Hours</span>
                  <span className="detail-value">{admin.contact_hours || 'N/A'}</span>
                </div>
              </div>

              <div className="admin-actions">
                <button
                  className="action-btn-privileges"
                  onClick={() => openPrivilegesModal(admin)}
                >
                  🔑 Privileges
                </button>
                <button
                  className="action-btn-edit"
                  onClick={() => openEditModal(admin)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="action-btn-delete"
                  onClick={() => handleDeactivateAdmin(admin.user_id)}
                >
                  🗑️ Deactivate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Admin</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleAddAdmin}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    className="form-input"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone_number"
                      className="form-input"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      required
                      maxLength="10"
                      placeholder="10-digit number"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="admin@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input
                    type="password"
                    name="password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength="6"
                    placeholder="Minimum 6 characters"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Assign Hostel *</label>
                    <select
                      name="hostel_id"
                      className="form-select"
                      value={formData.hostel_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Hostel</option>
                      {hostels.map(hostel => (
                        <option key={hostel.hostel_id} value={hostel.hostel_id}>
                          {hostel.hostel_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input
                      type="text"
                      name="designation"
                      className="form-input"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="Hostel Administrator"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Joining Date *</label>
                    <input
                      type="date"
                      name="joining_date"
                      className="form-input"
                      value={formData.joining_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Contact Hours</label>
                    <input
                      type="text"
                      name="contact_hours"
                      className="form-input"
                      value={formData.contact_hours}
                      onChange={handleInputChange}
                      placeholder="9:00 AM - 6:00 PM"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Office Location</label>
                  <input
                    type="text"
                    name="office_location"
                    className="form-input"
                    value={formData.office_location}
                    onChange={handleInputChange}
                    placeholder="Ground Floor, Admin Block"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Responsibilities</label>
                  <textarea
                    name="responsibilities"
                    className="form-textarea"
                    value={formData.responsibilities}
                    onChange={handleInputChange}
                    placeholder="Student welfare, maintenance coordination..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Add Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Admin</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleEditAdmin}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    className="form-input"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone_number"
                      className="form-input"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      required
                      maxLength="10"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">New Password (leave empty to keep current)</label>
                  <input
                    type="password"
                    name="password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter new password or leave empty"
                    minLength="6"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Assign Hostel *</label>
                    <select
                      name="hostel_id"
                      className="form-select"
                      value={formData.hostel_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Hostel</option>
                      {hostels.map(hostel => (
                        <option key={hostel.hostel_id} value={hostel.hostel_id}>
                          {hostel.hostel_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input
                      type="text"
                      name="designation"
                      className="form-input"
                      value={formData.designation}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Joining Date *</label>
                    <input
                      type="date"
                      name="joining_date"
                      className="form-input"
                      value={formData.joining_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Contact Hours</label>
                    <input
                      type="text"
                      name="contact_hours"
                      className="form-input"
                      value={formData.contact_hours}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Office Location</label>
                  <input
                    type="text"
                    name="office_location"
                    className="form-input"
                    value={formData.office_location}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Responsibilities</label>
                  <textarea
                    name="responsibilities"
                    className="form-textarea"
                    value={formData.responsibilities}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Update Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Privileges Modal */}
      {showPrivilegesModal && selectedAdmin && (
        <div className="modal-overlay" onClick={() => setShowPrivilegesModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                🔑 Manage Privileges - {selectedAdmin.full_name}
              </h2>
              <button className="modal-close" onClick={() => setShowPrivilegesModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-subtitle">
                Control which pages this admin can access and what actions they can perform.
              </p>

              <table className="privileges-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th className="checkbox-cell">View</th>
                    <th className="checkbox-cell">Edit</th>
                    <th className="checkbox-cell">Delete</th>
                    <th className="checkbox-cell">Approve</th>
                  </tr>
                </thead>
                <tbody>
                  {availablePages.map((page, index) => {
                    const privilege = adminPrivileges.find(p => p.page_id === page.page_id);
                    const status = getPrivilegeStatus(privilege);

                    const prevPage = availablePages[index - 1];
                    const showCategoryHeader = !prevPage || prevPage.page_category !== page.page_category;

                    return (
                      <React.Fragment key={page.page_id}>
                        {showCategoryHeader && (
                          <tr>
                            <td colSpan="5" className="privilege-category-header">
                              {page.page_category}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <div className="page-name">
                              <span className="page-icon">{page.page_icon}</span>
                              {page.page_name}
                            </div>
                          </td>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              className="privilege-checkbox"
                              checked={status.view}
                              onChange={(e) => handlePrivilegeChange(page.page_id, 'can_view', e.target.checked ? 1 : 0)}
                            />
                          </td>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              className="privilege-checkbox"
                              checked={status.edit}
                              onChange={(e) => handlePrivilegeChange(page.page_id, 'can_edit', e.target.checked ? 1 : 0)}
                            />
                          </td>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              className="privilege-checkbox"
                              checked={status.delete}
                              onChange={(e) => handlePrivilegeChange(page.page_id, 'can_delete', e.target.checked ? 1 : 0)}
                            />
                          </td>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              className="privilege-checkbox"
                              checked={status.approve}
                              onChange={(e) => handlePrivilegeChange(page.page_id, 'can_approve', e.target.checked ? 1 : 0)}
                            />
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowPrivilegesModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-submit" onClick={saveAdminPrivileges}>
                Save Privileges
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageAdmin;